import { Router } from '@angular/router';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { combineLatest, Observable, map, switchMap, take } from 'rxjs';

import {
  ControlData,
  FinnairDocumentItem,
  FinnairEmergencyContact,
  FinnairFrequentFlyerCardItem,
  FinnairOrder,
  FinnairPassengerItem,
  FinnairServices,
} from '@fcom/dapi/api/models';
import { QualtricsSurveys } from '@fcom/common/qualtrics/qualtrics.utils';
import { CommonBookingFeatureState, orderData, orderDataPrimaryPassenger } from '@fcom/common-booking';
import { FeedbackService } from '@fcom/common/services/feedback/feedback.service';
import { ButtonTheme, ModalButtons } from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';
import { TierBenefitService } from '@fcom/core/services/tier-benefit/tier-benefit.service';
import { TierLevelBenefits } from '@fcom/core/interfaces/tier-benefit.interface';
import { FrequentFlyerTierLevel } from '@fcom/common-booking/interfaces/utils.interface';
import { getCMSTier } from '@fcom/common-booking/utils/service.utils';

import { DeactivationGuarded } from '../services/passenger-details-deactivate.guard';

type PassengerTierLevel = Record<string, string>;

export type PassengerTierData = Record<
  string,
  {
    tierLabel: string;
    benefits: string[];
  }
>;

type ServiceDefinition = {
  id?: string;
  displayName?: string;
};

type PassengerServiceHolder = {
  services?: ServiceDefinition[];
};

type SegmentServiceHolder = {
  passengers?: PassengerServiceHolder[];
};

type BoundServiceHolder = {
  segments?: SegmentServiceHolder[];
};

type ServiceItem = {
  bounds?: BoundServiceHolder[];
};

export type PassengerEmdRow = {
  emdId: string;
  serviceName?: string;
};

@Component({
  selector: 'fin-mmb-passenger-details-route',
  templateUrl: './passenger-details-route.component.html',
  styleUrls: ['../../../../common-booking/assets/helper-classes.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageBookingPassengerDetailsRouteComponent implements DeactivationGuarded, OnInit, OnDestroy {
  private static readonly ALLOWED_DOCUMENT_ID_PREFIX = '105';

  private store$ = inject<Store<CommonBookingFeatureState>>(Store);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private feedbackService = inject(FeedbackService);
  private languageService = inject(LanguageService);
  private tierBenefitService = inject(TierBenefitService);

  readonly ModalButtons: typeof ModalButtons = ModalButtons;
  readonly ButtonTheme = ButtonTheme;

  unsavedChangesModalOpen = false;
  passengerInfosDirtyStatus: Record<string, boolean> = {};
  emergencyContactDirtyStatus = false;
  updatingPassengers: string[] = [];
  nextUrl: string;
  booking$: Observable<FinnairOrder>;
  controlData$: Observable<ControlData>;
  mainPaxInfo$: Observable<FinnairPassengerItem>;
  emergencyContact$: Observable<FinnairEmergencyContact>;
  allFrequentFlyerCards$: Observable<FinnairFrequentFlyerCardItem[]>;
  passengerTierLevel$: Observable<PassengerTierLevel>;
  tierBenefits$: Observable<TierLevelBenefits>;
  passengerTierData$: Observable<PassengerTierData>;
  emdRowsByPaxId$: Observable<Record<string, PassengerEmdRow[]>>;
  ticketRowsByPaxId$: Observable<Record<string, FinnairDocumentItem[]>>;

  ngOnInit(): void {
    this.booking$ = this.store$.pipe(orderData());
    this.controlData$ = this.booking$.pipe(map((booking) => booking.controlData));
    this.ticketRowsByPaxId$ = this.booking$.pipe(map((booking) => this.buildTicketRowsByPaxId(booking)));
    this.emdRowsByPaxId$ = this.booking$.pipe(map((booking) => this.buildEmdRowsByPaxId(booking)));
    this.mainPaxInfo$ = this.store$.pipe(orderDataPrimaryPassenger());
    this.emergencyContact$ = this.store$.pipe(
      orderData(),
      map((order) => order?.emergencyContact)
    );
    this.feedbackService.setSurvey(QualtricsSurveys.MMB_FEEDBACK);
    this.feedbackService.setFeedbackWidgetVisibility(true);
    this.allFrequentFlyerCards$ = this.booking$.pipe(
      map(({ passengers }) => passengers.flatMap((pax) => pax.frequentFlyerCards ?? []))
    );

    this.passengerTierLevel$ = this.booking$.pipe(
      map(({ passengers }) =>
        passengers
          .flatMap((pax) => pax)
          .reduce((acc, passenger) => {
            const frequentFlyerCard = passenger.frequentFlyerCards?.find((card) => card.companyCode === 'AY');
            if (frequentFlyerCard) {
              acc[passenger.id] = getCMSTier(frequentFlyerCard.tierLevel?.toString());
            }
            return acc;
          }, {} as PassengerTierLevel)
      )
    );
    this.tierBenefits$ = this.passengerTierLevel$.pipe(
      switchMap((passengerTierLevel) => {
        const tierLevels = Object.values(passengerTierLevel).join(',');
        return this.tierBenefitService.getBenefitJson(tierLevels, this.languageService.localeValue);
      })
    );

    this.passengerTierData$ = combineLatest([
      this.tierBenefits$,
      this.passengerTierLevel$,
      this.booking$.pipe(map(({ passengers }) => passengers)),
    ]).pipe(
      map(([tierBenefits, passengerTierLevel, passengers]) => {
        return passengers
          .filter((passenger) => this.hasFinnairPlusCard(passenger.frequentFlyerCards))
          .map((passenger) => {
            const passengerId = passenger.id;
            const benefits = this.tierBenefitService.filterBenefits(tierBenefits[passengerTierLevel[passengerId]]);
            return benefits
              ? {
                  [passengerId]: {
                    benefits: benefits.map((benefit) =>
                      benefit.description.generalDescription?.replace(/<[^>]*>/g, '')
                    ),
                    tierLabel: this.getTierLabel(passenger.frequentFlyerCards),
                  },
                }
              : null;
          })
          .filter((passengerData) => passengerData)
          .reduce((acc, passengerData) => Object.assign(acc, passengerData), {});
      })
    );
  }

  ngOnDestroy(): void {
    this.feedbackService.setFeedbackWidgetVisibility(false);
  }

  @HostListener('window:beforeunload')
  canUnload(): boolean {
    return !this.hasDirtyComponents();
  }

  private hasDirtyComponents(): boolean {
    return (
      this.emergencyContactDirtyStatus ||
      Object.keys(this.passengerInfosDirtyStatus)
        .map((key) => this.passengerInfosDirtyStatus[key])
        .some(Boolean)
    );
  }

  canDeactivate(nextUrl: string): boolean | Observable<boolean> | Promise<boolean> {
    if (this.hasDirtyComponents() && !this.unsavedChangesModalOpen) {
      this.nextUrl = nextUrl;
      this.unsavedChangesModalOpen = true;
      this.cdr.markForCheck();
      return false;
    } else {
      return true;
    }
  }

  hasFinnairPlusCard(cards: FinnairFrequentFlyerCardItem[]): boolean {
    return (cards || []).some((c) => c.companyCode && c.companyCode.toLowerCase() === 'ay');
  }

  getFinnairPlusTier(cards: FinnairFrequentFlyerCardItem[]): FrequentFlyerTierLevel {
    return (cards || []).find((c) => c.companyCode && c.companyCode.toLowerCase() === 'ay')
      ?.tierLevel as FrequentFlyerTierLevel;
  }

  getTierLabel(cards: FinnairFrequentFlyerCardItem[]): string {
    const tier = this.getFinnairPlusTier(cards);
    switch (tier) {
      case FrequentFlyerTierLevel.PLUS:
        return 'MMB.tierBenefits.basic.heading';
      case FrequentFlyerTierLevel.SILVER:
        return 'MMB.tierBenefits.silver.heading';
      case FrequentFlyerTierLevel.GOLD:
        return 'MMB.tierBenefits.gold.heading';
      case FrequentFlyerTierLevel.PLATINUM:
        return 'MMB.tierBenefits.platinum.heading';
      case FrequentFlyerTierLevel.LUMO:
        return 'MMB.tierBenefits.lumo.heading';
      case FrequentFlyerTierLevel.JUNIOR:
        return 'MMB.tierBenefits.junior.heading';
      default:
        return undefined;
    }
  }

  getAccompanyingInfant(pax: FinnairPassengerItem): Observable<FinnairPassengerItem> {
    return this.booking$.pipe(
      take(1),
      map((booking) => booking.passengers.find((p) => pax.id === p.accompanyingTravelerId))
    );
  }

  getAccompanyingAdult(pax: FinnairPassengerItem): Observable<FinnairPassengerItem> {
    return this.booking$.pipe(
      take(1),
      map((booking) => booking.passengers.find((p) => p.id === pax.accompanyingTravelerId))
    );
  }

  setPassengerDirtyStatus(passengerId: string, dirty: boolean): void {
    this.passengerInfosDirtyStatus[passengerId] = dirty;
  }

  setEmergencyContactDirtyStatus(dirty: boolean): void {
    this.emergencyContactDirtyStatus = dirty;
  }

  leaveWithoutSaving(): void {
    this.router.navigateByUrl(this.nextUrl);
  }

  continueEditing(): void {
    this.unsavedChangesModalOpen = false;
  }

  setUpdatingPassenger(id: string, updating: boolean): void {
    if (updating) {
      this.updatingPassengers.push(id);
    } else {
      this.updatingPassengers = this.updatingPassengers.filter((i) => i != id);
    }
  }

  private hasAllowedDocumentId(id: string | null | undefined): boolean {
    return (
      typeof id === 'string' && id.startsWith(ManageBookingPassengerDetailsRouteComponent.ALLOWED_DOCUMENT_ID_PREFIX)
    );
  }

  private buildTicketRowsByPaxId(booking: FinnairOrder): Record<string, FinnairDocumentItem[]> {
    return booking.passengers.reduce<Record<string, FinnairDocumentItem[]>>((acc, pax) => {
      acc[pax.id] = (pax.etickets ?? []).filter((ticket) => this.hasAllowedDocumentId(ticket?.id));
      return acc;
    }, {});
  }

  private buildServiceDisplayNameById(services: FinnairServices | undefined): Map<string, string> {
    const mapById = new Map<string, string>();
    if (!services) {
      return mapById;
    }

    const allServiceItems: ServiceItem[] = [
      ...(services.included ?? []),
      ...(services.unpaid ?? []),
      ...(services.pending ?? []),
    ];

    const allServices: ServiceDefinition[] = allServiceItems
      .flatMap((serviceItem) => serviceItem.bounds ?? [])
      .flatMap((bound) => bound.segments ?? [])
      .flatMap((segment) => segment.passengers ?? [])
      .flatMap((pax) => pax.services ?? []);

    return allServices.reduce((serviceNameById, service) => {
      const id = service?.id;
      const displayName = service?.displayName;
      if (!id || !displayName) {
        return serviceNameById;
      }

      // Some service ids are composite (e.g. "TSM121:TSM131:TSM156") while EMD serviceIds can reference the
      // individual parts (e.g. "TSM156"); index each part to the same displayName to allow lookups by single id.
      id.split(':').forEach((serviceId) => {
        if (!serviceNameById.has(serviceId)) {
          serviceNameById.set(serviceId, String(displayName));
        }
      });
      return serviceNameById;
    }, mapById);
  }

  private buildEmdRowsByPaxId(booking: FinnairOrder): Record<string, PassengerEmdRow[]> {
    const serviceNameById = this.buildServiceDisplayNameById(booking.services);
    return booking.passengers.reduce<Record<string, PassengerEmdRow[]>>((acc, pax) => {
      acc[pax.id] = (pax.emds ?? []).flatMap((emd) => {
        const emdId = emd.id;
        if (!this.hasAllowedDocumentId(emdId)) {
          return [];
        }
        const serviceIds = emd.serviceIds ?? [];
        if (!serviceIds.length) {
          return [{ emdId }];
        }

        return serviceIds.map((serviceId) => {
          return {
            emdId,
            serviceName: serviceNameById.get(serviceId),
          };
        });
      });

      return acc;
    }, {});
  }
}
